/**
 * A pulled item as the note it becomes (part 38d log, table PL, rows LW10 and LW11; decisions 58, 76; C6): a reader's
 * comment on a shared chapter at its passage (or the whole chapter), in the Notebook when the chapter isn't one the link
 * shares; a reply, by the reader or the writer, in the thread of the note it answers when that note is this link's in the
 * same relay thread, else (a reader's) a first note in the Notebook. Nothing an item says decides more than that: its note
 * ID is derived (DP5), its author is the relay's label, its words are plain text (decision 59). Pure.
 */
import { CONTEXT, toHex } from '@cockatrice/core';
import type { ReadingItem } from '@cockatrice/core';
import type { NoteAnchor, NoteRecord, ShareLinkRecord } from '@cockatrice/storage';
import type { PulledItem } from './wire.ts';

/** What the note's making knows besides the item: its derived ID, the note a reply names (if stored here), the time. */
export interface ItemContext {
  /** DP5: the note ID the item becomes. */
  readonly id: string;
  /** For a reply: the stored note of the item it names, or undefined when none is here. */
  readonly answered: NoteRecord | undefined;
  /** Deep-fix r9, table PR: for a reply: the first note of `answered`'s thread (`answered` itself when it is one), if here. */
  readonly first: NoteRecord | undefined;
  /** Deep-fix r2, PL′1: for a comment: whether its chapter is in the book here (an entry, not in the trash). */
  readonly chapterHere: boolean;
  readonly now: number;
}

/** A comment's anchor as the note keeps it: the context cut to {@link CONTEXT} each side, the quote whole. */
function anchorOf(item: Extract<ReadingItem, { kind: 'comment' }>): NoteAnchor | null {
  const { anchor } = item;
  if (anchor === null) return null;
  return { range: anchor.range, quote: anchor.quote, before: anchor.before.slice(-CONTEXT), after: anchor.after.slice(0, CONTEXT) };
}

/**
 * Table PL, LW10, LW11: the note `item` of `record`'s link becomes, opened as `opened`, or null when it is no note to
 * store: a writer's comment (the writer only replies), or a writer's reply whose thread isn't here.
 */
export function noteOfItem(record: ShareLinkRecord, item: PulledItem, opened: ReadingItem, context: ItemContext): NoteRecord | null {
  const link = { link: record.id, thread: toHex(item.thread), item: toHex(item.id) };
  const base = {
    id: context.id,
    bookId: record.bookId,
    title: '',
    group: '',
    sourceKey: null,
    visibility: 'only-me' as const,
    resolved: false,
    text: opened.text,
    writtenAt: item.at,
    storedAt: context.now,
    editedAt: context.now,
    deletedAt: null,
    link,
  };
  const reader = { origin: 'reader' as const, author: opened.name };
  if (opened.kind === 'comment') {
    if (item.author === 'writer') return null;
    // Deep-fix r2, PL′1: a chapter shared and still in the book; one deleted since sends the comment to the Notebook.
    const shared = record.chapters.includes(opened.chapter) && context.chapterHere;
    return { ...base, ...reader, chapterId: shared ? opened.chapter : null, anchor: shared ? anchorOf(opened) : null, parentId: null };
  }
  const { answered, first } = context;
  // LW11, table PR: the thread of the note it names, when that note and its thread's first note are this link's, in this
  // relay thread. Deep-fix r2, PL′2: a thread the writer deleted (its first note) takes no more: a reply to it goes to
  // the Notebook. Deep-fix r9: a reply the writer deleted in a standing thread still names that thread.
  const ours = (note: NoteRecord | undefined): note is NoteRecord => note?.link?.link === record.id && note.link.thread === link.thread;
  const inThread = ours(answered) && ours(first) && first.parentId === null && first.deletedAt === null && (answered.parentId ?? answered.id) === first.id;
  const who = item.author === 'writer' ? { origin: 'writer' as const, author: '' } : reader;
  if (inThread) return { ...base, ...who, chapterId: first.chapterId, anchor: null, parentId: first.id };
  return item.author === 'writer' ? null : { ...base, ...reader, chapterId: null, anchor: null, parentId: null };
}
